import { Storage, StorageConfig } from '@ionic/storage-angular';
import { openDB } from 'idb';
import { remove as _remove } from 'lodash';
import { Observable, Subject } from 'rxjs';
import { IGenericStorage, IndexHealth } from 'src/app/shared/services/contracts/database/generic-storage';
import { IIndexMetaDataInfo } from 'src/app/shared/services/contracts/database/index-metadata-info';
import { Paginated } from 'src/app/shared/services/contracts/database/paginated';

import { reportStorageWarning } from './storage-telemetry';
export class AlbertaStorage extends Storage implements IGenericStorage {
  private _storageConfig: StorageConfig;

  constructor(
    config: StorageConfig,
    public isDeletable: boolean,
    public canRepairIndex = true
  ) {
    super(config);
    this._storageConfig = config;
  }

  public getItem(key: string): Promise<any> {
    return this.get(key);
  }

  public async getItems(keys: string[]): Promise<any[]> {
    if (keys?.length) {
      const values = await Promise.all(keys.map(key => this.get(key)));

      // remove empty values
      return values.filter(value => value !== null);
    }

    return [];
  }

  public async getExistingKeys(keys: string[]): Promise<string[]> {
    if (!keys?.length) {
      return [];
    }
    const found = await Promise.all(keys.map(async key => ((await this.get(key)) != null ? key : null)));
    return found.filter((key): key is string => key !== null);
  }

  public setItem(key: string, value: any): Promise<any> {
    return this.set(key, value);
  }

  // The guard lives here (not only in setItem) so that direct .set() writes are also
  // observed – SqliteStorage.set() routes through setItem() and was therefore covered,
  // while AlbertaStorage.set() previously bypassed the check. setItem() delegates to
  // this.set(), so there is exactly one check per write and no recursion.
  //
  // Note: this is the only method that uses super.* instead of this.* – it overrides the
  // base set(), so calling this.set() here would recurse into itself. The other adapters
  // (getItem→this.get, removeItem→this.remove, …) call methods this class does NOT
  // override, so this.* correctly reaches the base implementation there.
  public set(key: string, value: any): Promise<any> {
    // Optional chaining: AlbertaStorage can be constructed without a config
    // (see UnitOfWorkFactory), so never dereference _storageConfig unconditionally –
    // the diagnostic must not throw and block the write.
    const store = this._storageConfig?.storeName;
    if (!key) {
      reportStorageWarning(
        `[AlbertaStorage] set called without key for store ${store}`,
        new Error(`Missing key [alberta:${store}]`),
        { storage: 'alberta', store, valueType: typeof value }
      );
    }
    if (value && !value._id) {
      reportStorageWarning(
        `[AlbertaStorage] set called without value._id for store ${store}, key: ${key}`,
        new Error(`Missing _id in value [alberta:${store}]`),
        {
          storage: 'alberta',
          store,
          key,
          valueType: typeof value,
          valueKeys: typeof value === 'object' ? Object.keys(value).slice(0, 30) : [],
        }
      );
    }
    return super.set(key, value);
  }

  search(query: string, params?: any[]): Promise<any> {
    return;
  }
  vacuum(): Promise<void> {
    return;
  }
  executeBatch(batch: any[]): Promise<any> {
    return;
  }
  repairIndex(): void {
    return;
  }
  indexHealth(): Promise<IndexHealth> {
    return Promise.resolve({ healthy: true });
  }

  readIndexFieldMetaInfo(item): IIndexMetaDataInfo {
    return null;
  }

  public async setItems(itemsForDb: { items: any[]; deletable: boolean }): Promise<any> {
    const archived = _remove(itemsForDb.items, item => item.archived || (item.metadata && item.metadata.archived));
    if (archived?.length) {
      if (itemsForDb.deletable) {
        await Promise.all(archived.map(item => this.removeItem(item._id)));
      } else {
        itemsForDb.items.push(...archived);
      }
    }

    if (itemsForDb.items.length) {
      return Promise.all(itemsForDb.items.map(item => this.set(item._id, item)));
    }
  }

  public removeItem(key: string): Promise<any> {
    return this.remove(key);
  }

  public async removeItems(key: string): Promise<any> {
    try {
      const keys = await this.getAllKeys();

      await Promise.all(
        keys?.filter(existingKey => existingKey.includes(key)).map(existingKey => this.removeItem(existingKey))
      );
    } catch (error) {
      window.logger.error('[AlbertaStorage]: Failed to get all keys', error);
    }
  }

  public getAll(): Observable<any> {
    const subject = new Subject();
    (async () => {
      await this.forEach(value => {
        subject.next(value);
      });
      subject.complete();
    })();

    return subject.asObservable();
  }

  public ready(): Promise<LocalForage> {
    return null;
  }

  public async getAllKeys(): Promise<string[]> {
    const db = await openDB(this._storageConfig.name, 2);
    const keys = await db.getAllKeys(this._storageConfig.storeName);

    return keys.map(key => key.toString());
  }

  getPaginated(chunkSize: number, offset: number): Promise<Paginated> {
    throw new Error('Method not implemented.');
  }

  public async raw({ sql }: { sql: string }): Promise<any> {
    return;
  }
}
